import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchAdminRules, fetchCategories, createCategory, updateCategory, createRule, updateRule, publishRule, archiveRule } from '../../../../app/features/rulesRegulationsSlice';
import { toast } from 'react-toastify';
import RuleStatusBadge from '../Shared/RuleStatusBadge';
import RuleFormModal from './RuleFormModal';
import CategoryFormModal from './CategoryFormModal';
import AcknowledgementsModal from './AcknowledgementsModal';
import { RulesRegulationsService } from '../../../../services/rulesRegulationsService';
import PageHeadingSection from '../../../common/pageheading/PageHeadingSection';
import Breadcrumbs from '../../../common/BreadCrum/BreadCrum';
import CommonTable from '../../../common/table/ComonTable';
import { faPlus } from '@fortawesome/free-solid-svg-icons';

const AdminRulesDashboard = () => {
  const dispatch = useDispatch();
  const { adminRules, categories, loading } = useSelector((state) => state.rulesRegulations || { adminRules: {}, categories: {} });
  const auth = useSelector((state) => state.auth); // Need auth for userId

  const [activeTab, setActiveTab] = useState('rules');
  const [isRuleModalOpen, setIsRuleModalOpen] = useState(false);
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const [isAckModalOpen, setIsAckModalOpen] = useState(false);
  const [ackRule, setAckRule] = useState(null);

  useEffect(() => {
    dispatch(fetchCategories());
    const siteId = auth?.user?.siteId || auth?.user?.SiteId || 1;
    dispatch(fetchAdminRules({ Page: 1, PageSize: 20, SiteId: siteId }));
  }, [dispatch, auth]);

  const handleCreateNew = () => {
    setSelectedItem(null);
    if (activeTab === 'rules') {
      setIsRuleModalOpen(true);
    } else {
      setIsCategoryModalOpen(true);
    }
  };

  const readFileAsDataUrl = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

  const handleRuleSubmit = async (data) => {
    const userId = auth?.user?.id || 1;
    const siteId = auth?.user?.siteId || auth?.user?.SiteId || 1;

    const payload = {
      SiteId: siteId,
      WingId: data.wingId ? Number(data.wingId) : null,
      FloorId: data.floorId ? Number(data.floorId) : null,
      CategoryId: Number(data.categoryId),
      Title: data.title,
      Description: data.description,
      Priority: data.priority || 'Normal',
      EffectiveFrom: data.effectiveFrom,
      EffectiveTo: data.effectiveTo || null,
      RequiresAcknowledgement: !!data.requiresAcknowledgement,
      Status: data.status || 'Draft'
    };

    if (data.attachment instanceof File) {
      payload.AttachmentFile = await readFileAsDataUrl(data.attachment);
      payload.AttachmentName = data.attachment.name;
    } else if (data.existingAttachmentUrl) {
      payload.AttachmentFile = data.existingAttachmentUrl;
      payload.AttachmentName = data.existingAttachmentName || null;
    }

    try {
      let response;
      let targetRuleId = selectedItem ? (selectedItem.id || selectedItem.Id) : null;

      if (selectedItem) {
        response = await dispatch(updateRule({ id: targetRuleId, formData: payload, userId })).unwrap();
      } else {
        response = await dispatch(createRule({ formData: payload, userId })).unwrap();
        targetRuleId = response?.id || response?.Id || response?.data?.id || response?.Data?.Id;
      }

      if (response?.isSuccess || response?.success || response?.IsSuccess) {
        if (data.publishAfterSave && targetRuleId) {
          try {
            const pubResponse = await dispatch(publishRule({ id: targetRuleId, userId })).unwrap();
            if (pubResponse?.isSuccess || pubResponse?.success || pubResponse?.IsSuccess) {
              toast.success("Rule saved and published as new version successfully!");
            } else {
              toast.warn(pubResponse?.message || "Rule saved, but publishing the version returned a warning.");
            }
          } catch (pubErr) {
            toast.warn("Rule saved, but publishing the new version encountered an error.");
          }
        } else {
          toast.success(response?.message || response?.Message || `Rule ${selectedItem ? 'updated' : 'created'} successfully!`);
        }
        dispatch(fetchAdminRules({ Page: 1, PageSize: 20, SiteId: siteId }));
        setIsRuleModalOpen(false);
      } else {
        toast.error(response?.message || response?.Message || `Failed to ${selectedItem ? 'update' : 'create'} rule`);
      }
    } catch (error) {
      toast.error(typeof error === 'string' ? error : "An error occurred while saving the rule");
    }
  };

  const handleEditRule = async (rule) => {
    try {
      const response = await RulesRegulationsService.getRuleById(rule.id || rule.Id);
      const fullRuleData = response.data || response.Data || response;
      setSelectedItem(fullRuleData);
      setIsRuleModalOpen(true);
    } catch (error) {
      toast.error("Failed to load full rule details.");
      setSelectedItem(rule);
      setIsRuleModalOpen(true);
    }
  };

  const handlePublishRule = async (ruleId, targetVersion = null) => {
    const confirmMessage = targetVersion
      ? `Are you sure you want to publish Version v${targetVersion}? This will create a new version snapshot, notify affected residents via FCM, and reset user acknowledgements.`
      : "Are you sure you want to publish this rule? This will notify affected residents via FCM.";

    if (window.confirm(confirmMessage)) {
      const userId = auth?.user?.id || 1;
      const siteId = auth?.user?.siteId || auth?.user?.SiteId || 1;
      try {
        const response = await dispatch(publishRule({ id: ruleId, userId })).unwrap();
        if (response?.isSuccess || response?.success || response?.IsSuccess) {
          toast.success(response?.message || response?.Message || (targetVersion ? `Version v${targetVersion} published successfully!` : "Rule published successfully!"));
          dispatch(fetchAdminRules({ Page: 1, PageSize: 20, SiteId: siteId }));
        } else {
          toast.error(response?.message || response?.Message || "Failed to publish rule.");
        }
      } catch (error) {
        toast.error(typeof error === 'string' ? error : "An error occurred while publishing.");
      }
    }
  };

  const handleArchiveRule = async (ruleId) => {
    if (window.confirm("Are you sure you want to archive this rule? It will no longer be active.")) {
      const userId = auth?.user?.id || 1;
      const siteId = auth?.user?.siteId || auth?.user?.SiteId || 1;
      try {
        const response = await dispatch(archiveRule({ id: ruleId, userId })).unwrap();
        if (response?.isSuccess || response?.success || response?.IsSuccess) {
          toast.success(response?.message || response?.Message || "Rule archived successfully!");
          dispatch(fetchAdminRules({ Page: 1, PageSize: 20, SiteId: siteId }));
        } else {
          toast.error(response?.message || response?.Message || "Failed to archive rule.");
        }
      } catch (error) {
        toast.error(typeof error === 'string' ? error : "An error occurred while archiving.");
      }
    }
  };

  const handleViewAcknowledgements = (rule) => {
    setAckRule(rule);
    setIsAckModalOpen(true);
  };

  const handleEditCategory = (cat) => {
    setSelectedItem(cat);
    setIsCategoryModalOpen(true);
  };

  const handleCategorySubmit = async (data) => {
    const userId = auth?.user?.id || 1;
    try {
      let response;
      if (selectedItem) {
        response = await dispatch(updateCategory({ id: selectedItem.id || selectedItem.Id, data, userId })).unwrap();
      } else {
        response = await dispatch(createCategory({ data, userId })).unwrap();
      }

      if (response?.isSuccess || response?.success || response?.IsSuccess) {
        toast.success(response?.message || response?.Message || `Category ${selectedItem ? 'updated' : 'created'} successfully!`);
        dispatch(fetchCategories());
        setIsCategoryModalOpen(false);
      } else {
        toast.error(response?.message || response?.Message || `Failed to ${selectedItem ? 'update' : 'create'} category`);
      }
    } catch (error) {
      toast.error(typeof error === 'string' ? error : "An error occurred");
    }
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Rules & Regulations Management</h1>
          <p className="text-sm text-gray-500">Manage categories, guidelines, rule revisions, and user acknowledgements.</p>
        </div>
        <button
          onClick={handleCreateNew}
          className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg font-medium shadow-sm transition-colors flex items-center"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          {activeTab === 'rules' ? 'Create Rule' : 'Create Category'}
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px px-6" aria-label="Tabs">
            <button
              onClick={() => setActiveTab('rules')}
              className={`w-1/4 py-4 px-1 text-center border-b-2 font-medium text-sm transition-colors ${activeTab === 'rules'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
            >
              Rules
            </button>
            <button
              onClick={() => setActiveTab('categories')}
              className={`w-1/4 py-4 px-1 text-center border-b-2 font-medium text-sm transition-colors ${activeTab === 'categories'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
            >
              Categories
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {loading && <div className="text-center py-10"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div></div>}

          {activeTab === 'rules' && !loading && (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title & Version</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Priority</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {adminRules?.data?.length > 0 ? (
                    adminRules.data.map((rule) => {
                      const versionNum = rule.currentVersion || rule.CurrentVersion || 1;
                      const status = rule.status || rule.Status;
                      return (
                        <tr key={rule.id || rule.Id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">{rule.title || rule.Title}</div>
                            <div className="text-xs text-gray-500 flex items-center gap-2 mt-1">
                              <span>Level: {rule.ruleLevel || rule.RuleLevel}</span>
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-semibold bg-indigo-50 text-indigo-700 border border-indigo-100">
                                v{versionNum}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{rule.categoryName || rule.CategoryName}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${(rule.priority || rule.Priority) === 'High' ? 'bg-orange-100 text-orange-800' : 'bg-blue-100 text-blue-800'}`}>
                              {rule.priority || rule.Priority}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <RuleStatusBadge status={status} />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            {status === 'Draft' ? (
                              <button
                                onClick={() => handlePublishRule(rule.id || rule.Id)}
                                className="text-green-600 hover:text-green-900 mx-2 transition-colors font-semibold"
                                title="Publish rule (v1)"
                              >
                                Publish
                              </button>
                            ) : status === 'Published' ? (
                              <button
                                onClick={() => handlePublishRule(rule.id || rule.Id, versionNum + 1)}
                                className="text-blue-600 hover:text-blue-800 mx-2 transition-colors font-semibold"
                                title={`Publish updated revision as Version v${versionNum + 1}`}
                              >
                                Publish v{versionNum + 1}
                              </button>
                            ) : null}

                            {status !== 'Archived' && (
                              <button
                                onClick={() => handleArchiveRule(rule.id || rule.Id)}
                                className="text-red-500 hover:text-red-700 mx-2 transition-colors font-semibold"
                              >
                                Archive
                              </button>
                            )}
                            <button
                              onClick={() => handleEditRule(rule)}
                              className="text-indigo-600 hover:text-indigo-900 mx-2 transition-colors"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleViewAcknowledgements(rule)}
                              className="text-gray-500 hover:text-indigo-600 mx-2 transition-colors"
                            >
                              View
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="5" className="px-6 py-10 text-center text-gray-500">
                        No rules found. Click "Create Rule" to add one.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {activeTab === 'categories' && !loading && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {categories?.data?.length > 0 ? (
                categories.data.map(cat => (
                  <div key={cat.id || cat.Id} className="bg-white p-5 border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow">
                    <h3 className="text-lg font-semibold text-gray-800">{cat.name || cat.Name}</h3>
                    <p className="text-gray-600 text-sm mt-2">{cat.description || cat.Description}</p>
                    <div className="mt-4 flex justify-between items-center">
                      <span className={`px-2 py-1 text-xs rounded-full ${(cat.isActive ?? cat.IsActive) ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {(cat.isActive ?? cat.IsActive) ? 'Active' : 'Inactive'}
                      </span>
                      <button
                        onClick={() => handleEditCategory(cat)}
                        className="text-indigo-600 hover:text-indigo-800 text-sm font-medium"
                      >
                        Edit
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <div className="col-span-full py-10 text-center text-gray-500">
                  No categories found. Click "Create Category" to add one.
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <RuleFormModal
        isOpen={isRuleModalOpen}
        onClose={() => setIsRuleModalOpen(false)}
        onSubmit={handleRuleSubmit}
        initialData={selectedItem}
      />

      <CategoryFormModal
        isOpen={isCategoryModalOpen}
        onClose={() => setIsCategoryModalOpen(false)}
        onSubmit={handleCategorySubmit}
        initialData={selectedItem}
      />

      <AcknowledgementsModal
        isOpen={isAckModalOpen}
        onClose={() => {
          setIsAckModalOpen(false);
          setAckRule(null);
        }}
        rule={ackRule}
      />
    </div>
  );
};

export default AdminRulesDashboard;
